import type { MutationTuple } from "@apollo/client";
import { FetchResult, OperationVariables } from "@apollo/client";
import { captureError } from "@sinch/core/config/sentry";
import { MessageSeverity } from "@sinch/core/entities/serverEnums";
import { extractMutationData, parseMutationErrorsFromData, throwIfNoMutationData } from "@sinch/utils/apollo";
import { applyErrorToForm } from "@sinch/utils/react-hook-form/errorHandling";
import { identity, prop, tap, values } from "ramda";
import { isNilOrEmpty, isNotNil } from "ramda-adjunct";
import { FieldValues } from "react-hook-form";
import { UseFormMutationReturn } from "@sinch/hooks/useFormMutation.types";
import { useMuiForm } from "@sinch/hooks/useMuiForm";
import { SinchMutations } from "@sinch/hooks/useMutationHandler";
import { useFormValidationToast } from "@sinch/hooks/useFormValidationToast";

export interface UseFormMutationSubmitProps<
  TData,
  TVariables = OperationVariables,
  TFieldValues extends FieldValues = FieldValues,
  TFormContext = any,
> {
  /**
   * Used mutation function for sending data from form
   */
  mutate: MutationTuple<TData, TVariables>[0];
  /**
   * Return of react-hook-form useForm, see https://react-hook-form.com/docs/useform
   */
  form: ReturnType<typeof useMuiForm<TFieldValues, TFormContext>>;
  /**
   * Use to map form field on variables data structure before mutaion call
   * @example ```
   * {variableMapper: ({id, user}) => ({id, input: user})}
   * ```
   */
  variableMapper?: (fieldValues: TFieldValues) => TVariables | Promise<TVariables>;
}

export const useFormMutationSubmit = <
  TData extends SinchMutations,
  TVariables = OperationVariables,
  TFieldValues extends FieldValues = FieldValues,
>({
  mutate,
  form,
  variableMapper = identity as any,
}: UseFormMutationSubmitProps<TData, TVariables, TFieldValues>) => {
  const showValidationToast = useFormValidationToast();

  const handleSubmit: UseFormMutationReturn<TData, TVariables, TFieldValues>["handleSubmit"] = (options) => {
    const { variableMapper: overriddenVariableMapper, errorMapper } = options || {};
    return new Promise<TData>((resolve, reject) =>
      form.handleSubmit(
        async (data) => {
          form.clearErrors();

          const applyErrorsToForm = (responseData: TData) => {
            const errorHandler = errorMapper ?? parseMutationErrorsFromData<TData>;
            const messages = errorHandler(responseData as TData);
            messages.forEach(applyErrorToForm(form as any, "serverValidation"));
            // Field errors from the server may land off-screen, so announce them (form-level messages are shown elsewhere)
            if (messages.some(({ severity, field }) => severity === MessageSeverity.Error && isNotNil(field))) {
              showValidationToast();
            }
          };

          try {
            const requestVariables = await (overriddenVariableMapper ?? variableMapper)(data);

            return mutate({ variables: requestVariables })
              .then(tap(fillServerErrors(form)))
              .then(extractMutationData<TData>)
              .then(tap(throwIfNoMutationData))
              .then(tap(applyErrorsToForm))
              .then(resolve)
              .catch((reason) => {
                form.clearErrors();
                form.setError("root.serverError", { type: "serverError" });
                reject(null);
                if (import.meta.env.MODE !== "production") {
                  console.error(reason);
                } else {
                  captureError(reason);
                }
                return null;
              });
          } catch (e) {
            console.error(e);
            return null;
          }
        },
        () => {
          // Client-side validation failed: the invalid field is highlighted, the toast tells the user to look for it.
          // The promise stays pending, callers do not catch it and a rejection would surface as an unhandled one
          showValidationToast();
        }
      )()
    );
  };

  return handleSubmit;
};

export const fillServerErrors =
  <TData, TFieldValues extends FieldValues = FieldValues, TFormContext = any>(
    form: ReturnType<typeof useMuiForm<TFieldValues, TFormContext>>
  ) =>
  ({ errors, data: responseData }: FetchResult<TData>) => {
    if (errors) {
      form.clearErrors();
      Object.entries(errors).forEach(([key, value]) => {
        form.setError(`root.serverError-${key}`, {
          type: "serverError",
          message: value?.message,
        });
      });
    }
    if (responseData) {
      values(responseData).forEach((mutationResponse) => {
        if (prop("result", mutationResponse) === false && isNilOrEmpty(prop("messages", mutationResponse))) {
          form.setError(`root.responseError`, { type: "responseError" });
        }
      });
    }
  };
